import { DebugLogger } from '@iconik-io/core/src/debug_logger/debug_logger';
import {
  type AssetsAssetCreateSchema,
  httpPostAssets,
} from '@iconik-io/iconik-api/src/services/assets';
import { httpPostAuthEmbedToken } from '@iconik-io/iconik-api/src/services/auth';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { getLatestActiveVersionId } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import { updateEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';
import { normalizeEntities } from '@iconik-io/shared-lib/src/redux/entities/utils';

import { cuttingRoomBridge } from '../../components/cutting_room/cutting_room_bridge';
import {
  buildCrl,
  buildCuttingRoomExternalId,
  CUTTING_ROOM_CRL_VERSION_PINNING_ENABLED,
} from '../../components/cutting_room/cutting_room_config';
import {
  addToProjectBin,
  createProject,
  openProject,
  type ProjectBinItem,
} from '../../components/cutting_room/cutting_room_post_message';
import { CUTTING_ROOM_PROJECT_PANEL } from '../../constants/data';
import type { AppDispatch, AppState } from '../../store/types';
import { errorMessage, successMessage } from '../messages/thunks';
import { searchActions } from '../pages/search/actions';
import { openPanel } from '../panels/slice';
import { getCurrentSystemDomainId } from '../system_domains/selectors';
import { selectCuttingRoomBaseUrl } from './base_url';
import {
  clearCuttingRoomPendingProject,
  setCuttingRoomPendingProject,
  setCuttingRoomProjectId,
} from './slice';

const DEFAULT_FPS = 25;

/**
 * How long a created project gets to load once it has been opened. Past it the
 * panel reveals the iframe anyway, showing whatever CuttingRoom has got to.
 */
const PROJECT_LOAD_TIMEOUT_MS = 15_000;

const logger = DebugLogger.getInstance('cutting_room');

export const COLLECTIONS_UNSUPPORTED_ERROR =
  'Collections cannot be added to the project bin yet';

export const CREATE_ALREADY_PENDING_ERROR =
  'A project is already being created';

export const SYSTEM_DOMAIN_UNAVAILABLE_ERROR = 'System domain is not available';

/**
 * CuttingRoom has no project bin representation for collections yet, so a
 * selection containing one is rejected as a whole rather than silently
 * dropping items.
 */
function assertAllAssets(items: SearchItemEntity[]): void {
  if (items.some((item) => item.object_type !== OBJECT_TYPE_ASSETS)) {
    throw new Error(COLLECTIONS_UNSUPPORTED_ERROR);
  }
}

/**
 * Map search items to CuttingRoom project bin items.
 *
 * The version id is taken from the `versions` already carried by the search
 * item, so we never fetch per item and a large multi-select stays a single
 * postMessage call. When the item has no versions — or while
 * `CUTTING_ROOM_CRL_VERSION_PINNING_ENABLED` is off — the segment is omitted
 * and CuttingRoom resolves and pins the latest version itself.
 */
function toProjectBinItems(
  items: SearchItemEntity[],
  systemDomainId: string,
): ProjectBinItem[] {
  return items.map((item) => ({
    url: buildCrl({
      assetId: item.id,
      systemDomainId,
      versionId: CUTTING_ROOM_CRL_VERSION_PINNING_ENABLED
        ? getLatestActiveVersionId(item.versions)
        : undefined,
    }),
    title: item.title ?? 'Untitled',
    contentType:
      ('media_type' in item && typeof item.media_type === 'string'
        ? item.media_type
        : undefined) ?? 'video',
  }));
}

function getSystemDomainId(state: AppState): string {
  const systemDomainId = getCurrentSystemDomainId(state);
  if (!systemDomainId) {
    throw new Error(SYSTEM_DOMAIN_UNAVAILABLE_ERROR);
  }

  return systemDomainId;
}

function getCuttingRoomBaseUrl(state: AppState): string {
  const baseUrl = selectCuttingRoomBaseUrl(state);
  if (!baseUrl) {
    throw new Error('CuttingRoom URL is not configured');
  }

  return baseUrl;
}

/**
 * Mint a short-lived, single-use embed token for the logged in user.
 *
 * CuttingRoom exchanges it for a session of its own by calling Iconik's
 * token exchange endpoint, so the user is never shown a CuttingRoom login form.
 */
export const fetchCuttingRoomEmbedToken =
  () =>
  async (dispatch: AppDispatch): Promise<string> => {
    const response = await dispatch(httpPostAuthEmbedToken({}));
    const embedToken = response.body.embed_token;

    if (!embedToken) {
      throw new Error('Iconik did not return an embed token');
    }

    logger.debug('Minted CuttingRoom embed token, expires:', {
      expires: response.body.expires,
    });

    return embedToken;
  };

/**
 * Create a new CuttingRoom project, create a corresponding NLE_PROJECT asset
 * in Iconik, and add the selected items to the project bin.
 *
 * The create is marked pending from the start, which keeps the panel on a
 * loading state until `useCuttingRoomIframe` sees the new project load, or
 * `PROJECT_LOAD_TIMEOUT_MS` after it was opened.
 *
 * Flow:
 * 1. Call the CuttingRoom `POST /projects` API via the iframe `call_api` bridge,
 *    passing the selected items as `projectBinItems`.
 * 2. Store the project ID in Redux and open it in the embedded player.
 * 3. Create an Iconik NLE_PROJECT asset with `external_id` linking back to the
 *    CuttingRoom project (`cutting_room:<region>:<tenant>:project:<id>`).
 */
export const createCuttingRoomProject =
  (title: string, items: SearchItemEntity[], collectionId?: string) =>
  async (dispatch: AppDispatch, getState: () => AppState): Promise<void> => {
    // Two creates would race each other's `open_project` in the one iframe.
    // Returning before the `try` leaves the running create's pending state
    // alone.
    if (getState().cuttingRoom.pendingProject) {
      dispatch(errorMessage({ text: CREATE_ALREADY_PENDING_ERROR }));
      return;
    }

    // Once the new project is open, its load or the load deadline ends the
    // pending state, which by then may belong to a later create.
    let loadDeadlineSet = false;

    try {
      logger.debug('Creating CuttingRoom project:', {
        title,
        itemCount: items.length,
        collectionId,
      });
      assertAllAssets(items);

      dispatch(setCuttingRoomPendingProject({ title }));
      dispatch(openPanel(CUTTING_ROOM_PROJECT_PANEL));

      logger.debug('Waiting for CuttingRoom iframe to be ready…');
      await cuttingRoomBridge.waitForReady();
      logger.debug('CuttingRoom iframe is ready');

      const state = getState();
      const baseUrl = getCuttingRoomBaseUrl(state);

      const projectBinItems = toProjectBinItems(
        items,
        getSystemDomainId(state),
      );

      logger.debug('Calling CuttingRoom API to create project…');
      const cuttingRoomProject = await createProject({
        title,
        fps: DEFAULT_FPS,
        projectBinItems,
      });
      logger.debug('CuttingRoom project created:', cuttingRoomProject);

      const { projectId } = cuttingRoomProject;

      // Opened before the Iconik asset is created, so the panel doesn't wait on
      // that round trip.
      dispatch(setCuttingRoomPendingProject({ title, projectId }));
      dispatch(setCuttingRoomProjectId(projectId));
      openProject(projectId);
      logger.debug('Project opened in panel:', projectId);

      setTimeout(() => {
        // By now the project has loaded, or a later create owns the state.
        if (getState().cuttingRoom.pendingProject?.projectId !== projectId) {
          return;
        }
        logger.debug('CuttingRoom project did not load in time');
        dispatch(clearCuttingRoomPendingProject());
      }, PROJECT_LOAD_TIMEOUT_MS);
      loadDeadlineSet = true;

      const externalId = buildCuttingRoomExternalId(projectId, baseUrl);

      const body: AssetsAssetCreateSchema = {
        title,
        type: 'NLE_PROJECT',
        external_id: externalId,
        collection_id: collectionId,
      };

      logger.debug('Creating Iconik NLE_PROJECT asset…');
      const response = await dispatch(
        httpPostAssets(body, {
          assign_to_collection: collectionId !== undefined,
        }),
      );

      const newAsset = {
        ...response.body,
        person_ids: response.body.person_ids || undefined,
        media_type: 'nle_project',
        object_type: 'assets',
      };

      if (newAsset.id) {
        const searchItem: SearchItemEntity = {
          ...newAsset,
          id: newAsset.id,
          object_type: OBJECT_TYPE_ASSETS,
        };

        dispatch(
          updateEntities(
            normalizeEntities(searchItem, 'search_items').entities,
          ),
        );
        dispatch(searchActions.prependId(newAsset.id));
      }

      dispatch(
        successMessage({
          text: `CuttingRoom project "${title}" created`,
        }),
      );
    } catch (error) {
      logger.debug('Failed to create CuttingRoom project:', error);
      dispatch(
        errorMessage({
          text:
            error instanceof Error
              ? error.message
              : 'Failed to create CuttingRoom project',
        }),
      );
      if (!loadDeadlineSet) {
        dispatch(clearCuttingRoomPendingProject());
      }
    }
  };

/**
 * Add search items to the currently open CuttingRoom project's bin
 * via the `add_to_project_bin` postMessage action.
 */
export const addItemsToProjectBin =
  (items: SearchItemEntity[]) =>
  async (dispatch: AppDispatch, getState: () => AppState): Promise<void> => {
    try {
      assertAllAssets(items);

      const projectBinItems = toProjectBinItems(
        items,
        getSystemDomainId(getState()),
      );

      await addToProjectBin(projectBinItems);

      dispatch(
        successMessage({
          text: `Added ${items.length} item${items.length === 1 ? '' : 's'} to project bin`,
        }),
      );
    } catch (error) {
      dispatch(
        errorMessage({
          text:
            error instanceof Error
              ? error.message
              : 'Failed to add items to project bin',
        }),
      );
    }
  };
